import React from 'react';

import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/20px/icon_20px_delete_outline.svg';
import { ReactComponent as ResetIcon } from '@repo/common/assets/icons/20px/icon_20px_reset.svg';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/20px/icon_20px_cancel.svg';
import { BaseButton, ProgressBar, useT } from '@repo/common';

const ERROR_MESSAGES = {
  size: 'File exceeds the 50MB limit. Please upload a smaller file.',
  format: 'Unsupported file format. Please upload JPG, JPEG, PNG, or PDF.',
  network: 'Upload interrupted. Please try again.',
  corrupted: 'This file appears to be corrupted. Please try another file.',
  generic: 'Upload failed. Please try again.',
};

const ERROR_MESSAGES_KR = {
  size: '파일이 50MB 제한을 초과했습니다. 더 작은 파일을 업로드해 주세요.',
  format:
    '지원하지 않는 파일 형식입니다. JPG, JPEG, PNG 또는 PDF 파일을 업로드해 주세요.',
  network: '업로드가 중단되었습니다. 다시 시도해 주세요.',
  corrupted: '파일이 손상된 것 같습니다. 다른 파일을 시도해 주세요.',
  generic: '업로드에 실패했습니다. 다시 시도해 주세요.',
};

const FileRow = ({ fileData, onReset, onDelete, $isMobile }) => {
  const t = useT();

  if (!fileData) return null;

  const file = fileData.file;
  const fileTypeLabel = file.type?.split('/')?.pop()?.toUpperCase();

  const errorMessage =
    fileData.errorType &&
    t(
      ERROR_MESSAGES[fileData.errorType],
      ERROR_MESSAGES_KR[fileData.errorType],
    );

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';

    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${Math.round(kb)} KB`;
    }

    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  return (
    <div
      className={`flex flex-col p-5 items-center gap-4 bg-grayscale-gray00 w-full rounded-lg border ${
        fileData.status === 'failed'
          ? 'border-functional-failed03'
          : 'border-grayscale-gray02'
      }`}
    >
      <div className='flex items-center gap-3 bg-grayscale-gray00 w-full min-w-0'>
        <div className='w-[42px] h-[42px] aspect-square shrink-0 flex items-center justify-center border border-grayscale-gray02 rounded bg-grayscale-gray01'>
          <span className='typo-b12Strong text-text-placeholder w-full'>
            {fileTypeLabel}
          </span>
        </div>
        <div className='flex flex-col justify-center items-start text-left w-full min-w-0 gap-1'>
          <div className='w-full truncate typo-b12Strong tabletH:typo-b14Strong text-text-primary'>
            {file.name}
          </div>
          {fileData.status === 'failed' ? (
            <div className='flex items-center text-text-failed typo-b13'>
              {errorMessage}
            </div>
          ) : fileData.uploading ? (
            <div className='flex items-center text-text-placeholder typo-b13'>
              {formatFileSize(fileData.loaded)} {t('of', '/')}{' '}
              {formatFileSize(file.size)}
            </div>
          ) : (
            <div className='flex items-center text-text-placeholder typo-b13'>
              {formatFileSize(file.size)}
            </div>
          )}
        </div>
        <div className='shrink-0'>
          {fileData.status === 'failed' ? (
            <div className='flex gap-3'>
              <BaseButton
                type='text'
                size='s'
                onClick={onReset}
                className='h-auto min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent'
                icon={<ResetIcon />}
              />
              <BaseButton
                type='text'
                size='s'
                onClick={onDelete}
                className='h-auto min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent'
                icon={<CancelIcon />}
              />
            </div>
          ) : fileData.status === 'uploading' || fileData.progress < 100 ? (
            <BaseButton
              type='text'
              size='s'
              onClick={onDelete}
              className='h-auto min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent'
              icon={<CancelIcon />}
            />
          ) : (
            <BaseButton
              type='text'
              size='s'
              onClick={onDelete}
              className='h-auto min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent'
              icon={<DeleteIcon />}
            />
          )}
        </div>
      </div>

      {fileData.status !== 'failed' &&
        fileData.status !== 'success' &&
        fileData.progress !== undefined && (
          <div className='flex flex-col gap-1 w-full'>
            <ProgressBar progress={fileData.progress} />
          </div>
        )}
    </div>
  );
};

export default FileRow;
